import { ReactNode, createContext, useContext } from 'react';
import { helperForField, labelForField } from '../helpers';
import type { FormField } from '../types';

interface FieldShellProps {
  field: FormField;
  error?: string;
  children: ReactNode;
  /** Pass true for fields that supply their own label (e.g. Checkbox). */
  hideLabel?: boolean;
}

/**
 * Suppress per-field labels for an entire subtree. Used by the grid form
 * where each column header already names the field, so repeating
 * `Action`, `Reviewer`, etc. above every cell is noise.
 */
const FieldChromeContext = createContext<{ hideLabel: boolean }>({ hideLabel: false });

export const FieldChromeProvider = ({
  hideLabel = false,
  children,
}: {
  hideLabel?: boolean;
  children: ReactNode;
}) => (
  <FieldChromeContext.Provider value={{ hideLabel }}>{children}</FieldChromeContext.Provider>
);

/**
 * Shared label / helper-text / error-message frame around every widget.
 * Keeps individual widgets focused on their input mechanics.
 */
export const FieldShell = ({ field, error, children, hideLabel = false }: FieldShellProps) => {
  const chrome = useContext(FieldChromeContext);
  const suppressLabel = hideLabel || chrome.hideLabel;
  const label = labelForField(field);
  const helper = helperForField(field);

  return (
    <div className="space-y-1">
      {!suppressLabel && (
        <label className="form-label" htmlFor={field.title}>
          {label}
          {field.rqd && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="form-error">{error}</p>
      ) : helper ? (
        <p className="text-xs text-rgf-gray-500">{helper}</p>
      ) : null}
    </div>
  );
};
